import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Toolbar,
  Typography,
  Tooltip,
  TablePagination,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  PriceChange as FeeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { chalanAPI, vehicleTypeAPI } from '../services/chalanService';
import PageLoader from '../components/PageLoader';

function ChalanList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const orgContext = useOrg();
  const orgPrefix = `/${(orgContext?.orgCode || localStorage.getItem('orgCode') || 'rta').toLowerCase()}`;
  const { user } = useAuth();
  const [chalans, setChalans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [stats, setStats] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const statusColors = {
    pending: 'warning',
    issued: 'info',
    paid: 'success',
    cancelled: 'error',
    disputed: 'error',
    resolved: 'success',
  };

  // Fetch vehicle types for display
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const response = await vehicleTypeAPI.getVehicleTypes();
        setVehicleTypes(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching vehicle types:', err);
      }
    };
    fetchVehicleTypes();
  }, []);

  // Fetch chalans
  const fetchChalans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let params = {
        offset: page * rowsPerPage,
        limit: rowsPerPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await chalanAPI.getChalans(params);
      const data = response.data.results || response.data || [];
      setChalans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching chalans:', err);
      setError(err.response?.data?.detail || t('chalanList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, t]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await chalanAPI.getStatistics();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  }, []);

  // fetchChalans changes whenever filters change; fetchStats is stable (empty deps).
  // Split into two effects so stats is not re-fetched on every filter change.
  useEffect(() => {
    fetchChalans();
  }, [fetchChalans]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewChalan = (id) => {
    navigate(`${orgPrefix}/chalans/${id}`);
  };

  const handleManageFees = () => {
    navigate(`${orgPrefix}/fee-management`);
  };

  const canManageFees = user?.is_staff ||
    (user?.features && user.features.some(f => f.name === 'chalan_update'));

  const getVehicleTypeName = (vehicleTypeId) => {
    const vType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
    return vType ? vType.name : t('permitSearch.na');
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, minWidth: 0 }}>
      <Typography variant="h4" sx={{ marginBottom: '20px', fontWeight: 'bold' }}>
        {t('chalanList.pageTitle')}
      </Typography>

      {/* Statistics Cards */}
      {stats && (
        <>
          {/* Status count cards - one row */}
          <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('chalanList.totalChalans')}
                  </Typography>
                  <Typography variant="h5">
                    {stats.total_chalans || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('common.pending')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#ff9800' }}>
                    {stats.by_status?.pending || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('common.paid')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#4caf50' }}>
                    {stats.by_status?.paid || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('chalanList.issuedApproved')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#0288d1' }}>
                    {stats.by_status?.issued || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('chalanList.cancelledRejected')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#757575' }}>
                    {stats.by_status?.cancelled || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Calculation cards - second row, right-aligned */}
          <Grid container spacing={2} sx={{ marginBottom: '30px', justifyContent: 'flex-end' }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('chalanList.totalRevenue')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#1565c0' }}>
                    Rs. {parseFloat(stats.total_fees_amount || 0).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('chalanList.collected')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#4caf50' }}>
                    Rs. {parseFloat(stats.total_paid_amount || 0).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('chalanList.pendingCollection')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#f44336' }}>
                    Rs. {parseFloat(stats.pending_collection || 0).toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Error Alert */}
      {error && <Alert severity="error" sx={{ marginBottom: '20px' }}>{error}</Alert>}

      {/* Toolbar */}
      <Toolbar sx={{ backgroundColor: '#f5f5f5', borderRadius: '4px', mb: 2.5, p: 1.25, gap: 1, flexWrap: 'wrap' }}>
        <TextField
          label={t('chalanList.searchLabel')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 260px' } }}
        />

        <TextField
          select
          label={t('permitList.filterStatus')}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          size="small"
          SelectProps={{ native: true }}
          sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 150px' } }}
        >
          <option value="all">{t('permitList.allStatus')}</option>
          <option value="pending">{t('common.pending')}</option>
          <option value="issued">{t('common.issued')}</option>
          <option value="paid">{t('common.paid')}</option>
          <option value="cancelled">{t('chalanList.cancelledLabel')}</option>
        </TextField>

        <Box sx={{ flexGrow: 1 }} />

        {canManageFees && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FeeIcon />}
            onClick={handleManageFees}
            sx={{ marginRight: '10px' }}
          >
            {t('chalanList.manageFees')}
          </Button>
        )}

      </Toolbar>

      {/* Loading */}
      {loading && <PageLoader variant="table" rows={5} />}

      {/* Table */}
      {!loading && (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('chalanList.chalanNumber')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('chalanList.ownerName')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('permitSearch.cnicLabel')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('chalanList.carNumber')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('chalanList.vehicleType')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('chalanList.fee')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('chalanList.paidCol')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('permitList.status')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('permitList.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chalans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ padding: '40px' }}>
                    {t('chalanList.noChalans')}
                  </TableCell>
                </TableRow>
              ) : (
                chalans.map((chalan) => (
                  <TableRow key={chalan.id} hover>
                    <TableCell>{chalan.chalan_number}</TableCell>
                    <TableCell>{chalan.owner_name}</TableCell>
                    <TableCell>{chalan.owner_cnic}</TableCell>
                    <TableCell>{chalan.car_number}</TableCell>
                    <TableCell>{getVehicleTypeName(chalan.vehicle_type)}</TableCell>
                    <TableCell align="right">
                      Rs. {parseFloat(chalan.fees_amount).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      Rs. {parseFloat(chalan.paid_amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`common.${chalan.status}`, { defaultValue: chalan.status }).toUpperCase()}
                        color={statusColors[chalan.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('permitList.viewDetails')}>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewChalan(chalan.id)}
                        >
                          {t('permitList.view')}
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50]}
            component="div"
            count={stats?.total_chalans || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}
    </Box>
  );
}

export default ChalanList;

